/**
 * Staff detail screen (DNKADM-0053 U2; cards since DNKADM-0494) — one `v2_staff` member as
 * read-only cards, each with ONE Edit that opens a right-side {@link EditDrawer} with one
 * Save. Nothing outside a drawer saves. Routed at `/staff/:staffId` under `RequireAdmin`.
 * The card and drawer shells are the service page's (DNKADM-0493), used as they are.
 *
 * **Header.** Name, status chip (Active / Inactive / Removed) and a More menu with the
 * existing actions: Deactivate or Reactivate, and (OWNER/ADMIN) Remove or Restore — the same
 * calls, refusals and toasts as the Staff list.
 *
 * **Setup health (DNKADM-0492 / DNKAPI-0714).** {@link StaffSetupCard} stays at the top,
 * unchanged; each in-page fix link opens the drawer that fixes it.
 *
 * **Cards, top to bottom**, each drawer reusing the existing editor and its same request:
 *  1. Basics — name, email, phone, login. Drawer: the staff form's basics slice
 *     ({@link StaffFields}); the same staff PUT the old Edit details dialog sent.
 *  2. Where they work — assigned locations. Drawer: the form's locations slice; the same
 *     staff PUT, then the assignments PUT when they changed.
 *  3. Weekly hours — one line per location (e.g. "Tue to Thu 10 AM to 7 PM"). Drawer:
 *     {@link StaffScheduleGrid}, a tab per location; Save PUTs the shown location's week.
 *  4. Days off — the date exceptions. Drawer: {@link StaffDateExceptionsPanel} unchanged;
 *     each day off is its own add or remove, so the footer is one Done (the 0493 Special
 *     prices precedent).
 *  5. Booking calendar — the calendar by name and sync on/off. Drawer:
 *     {@link StaffCalendarSyncPanel}, with the calendar test inside it.
 *  6. Limits — appointment caps; one line when none. Drawer: {@link StaffCapsPanel}; Save
 *     writes each changed period.
 *  7. Services they teach (DNKADM-0498) — the coach's services from the single staff GET
 *     (`services`, DNKAPI-0715), each a link to its service page. None: the card collapses to
 *     "None yet" with its Edit. A coach's services are set on each service's page, so this
 *     card has no drawer: its Edit goes to the Services screen (the setup card's
 *     "Go to Services" destination). An older API that omits the key shows "—".
 *
 * Reads: the record, its assigned locations, the Team & access roster (the login label,
 * shared with the Basics drawer), the date exceptions, the caps, the calendar list (the
 * calendar's name), and each assigned location's week (the same schedule GET, made per
 * location up front so every location has its line). A failed read shows "Couldn’t load"
 * on its card, never an empty value. A drawer's close re-reads the page, and the last
 * values stay on screen while it does.
 */
import { useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Chip,
  Link as MuiLink,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Icon } from '@/theme';
import { IconAction } from '@/components/IconAction';
import { useResource } from '@/hooks/useResource';
import { useToast } from '@/components/useToast';
import { PlanLimitDialog } from '@/components/PlanLimitDialog';
import { ApiError } from '@/api/errors';
import { serverMessage } from '@/api/serverMessage';
import { parsePlanLimit, v2ErrorCode, type PlanLimitInfo } from '@/api/v2Errors';
import {
  deactivateStaff,
  getStaffMember,
  getStaffSchedule,
  getStaffLocations,
  listStaffDateExceptions,
  listStaffCaps,
  reactivateStaff,
  restoreStaff,
  staffDisplayName,
  type StaffScheduleDay,
  type V2Staff,
} from '@/api/staff';
import { getStaffCalendars } from '@/api/staffCalendar';
import { listLocations, type V2Location } from '@/api/locations';
import { getPlatformMembers } from '@/api/platformMembers';
import { useOptionalOperator } from '@/auth/authHooks';
import { scrubBrand } from '@/utils/whitelabel';
import { EditDrawer } from '@/features/services/EditDrawer';
import { SummaryCard, SummaryRow } from '@/features/services/SummaryCard';
import { StaffScheduleGrid } from './StaffScheduleGrid';
import { StaffDateExceptionsPanel } from './StaffDateExceptionsPanel';
import { StaffCapsPanel } from './StaffCapsPanel';
import { StaffCalendarSyncPanel } from './StaffCalendarSyncPanel';
import { StaffFields } from './StaffDialog';
import { StaffSetupCard, type StaffSetupFixItem } from './StaffSetupCard';
import { RemoveStaffDialog, type RemoveStaffOutcome } from './RemoveStaffDialog';
import { restoreErrorMessage, staffRemovedMessage } from './staffError';
import type { StaffEditorHandle } from './staffEditorHandle';
import {
  CAP_PERIOD_LABELS,
  capLine,
  dayOffDate,
  dayOffLine,
  weeklyHoursSummary,
} from './staffSummaries';
import {
  resolveStaffLoginLabel,
  toStaffLoginOptions,
  type StaffLoginOption,
} from './staffLogins';
import { ScreenError } from '@/components/ScreenError';

/** The drawer each card opens. */
type StaffDrawer = 'basics' | 'where' | 'hours' | 'daysOff' | 'calendar' | 'limits';

const DRAWER_TITLES: Record<StaffDrawer, string> = {
  basics: 'Edit basics',
  where: 'Edit where they work',
  hours: 'Edit weekly hours',
  daysOff: 'Edit days off',
  calendar: 'Edit booking calendar',
  limits: 'Edit limits',
};

/** Which drawer fixes each setup item that is fixed on this page. */
const FIX_DRAWER: Record<StaffSetupFixItem, StaffDrawer> = {
  NO_LOCATION: 'where',
  NO_HOURS: 'hours',
  CALENDAR_NOT_LINKED: 'calendar',
  LOGIN_NOT_LINKED: 'basics',
};

const COULD_NOT_LOAD = 'Couldn’t load';
const LOADING = '—';
/** A field the record does not carry. */
const NONE = '—';

/** One location's week as loaded, or the read failed. */
type WeekRead = { ok: true; days: StaffScheduleDay[] } | { ok: false };

/**
 * The Basics card's login text: "Not linked" when there is no link, the roster label
 * (`name (email)`) when it resolves, and "—" when it cannot be resolved (the roster is
 * loading, failed, or the login has left it). The `authUserId` itself is NEVER shown.
 */
function loginText(authUserId: number | null, options: StaffLoginOption[] | null): string {
  if (authUserId === null) return 'Not linked';
  if (options === null) return LOADING;
  return resolveStaffLoginLabel(options, authUserId) ?? LOADING;
}

function locationLabel(loc: V2Location): string {
  return loc.name === '' ? loc.id : loc.name;
}

export function StaffDetail() {
  const { staffId = '' } = useParams<{ staffId: string }>();
  const showToast = useToast();
  const navigate = useNavigate();
  // Remove/Restore are OWNER/ADMIN (SET UP), as on the Staff list.
  const canManage = useOptionalOperator()?.isAdmin === true;

  const staff = useResource(
    useCallback(() => getStaffMember(staffId), [staffId]),
    [staffId],
  );

  // The staffer's assigned locations as full V2Location rows (the cards, the hours tabs and
  // the day-off location options).
  const assigned = useResource(
    useCallback(async (): Promise<V2Location[]> => {
      const [ids, all] = await Promise.all([
        getStaffLocations(staffId),
        listLocations({ status: 'active' }),
      ]);
      const order = new Map(all.map((l, i) => [l.id, i] as const));
      return all.filter((l) => ids.includes(l.id)).sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
    }, [staffId]),
    [staffId],
  );
  const assignedData = assigned.data;
  const assignedLocations = useMemo(() => assignedData ?? [], [assignedData]);

  // The staff logins this coach record could belong to (DNKADM-0362) — loaded ONCE here
  // and shared with the Basics drawer. A failure degrades to "no options".
  const logins = useResource(
    useCallback(async (): Promise<StaffLoginOption[]> => {
      const roster = await getPlatformMembers();
      return toStaffLoginOptions(roster.members);
    }, []),
    [],
  );
  const loginOptions = logins.data;

  const exceptions = useResource(
    useCallback(() => listStaffDateExceptions(staffId), [staffId]),
    [staffId],
  );
  const caps = useResource(
    useCallback(() => listStaffCaps(staffId), [staffId]),
    [staffId],
  );
  // The calendar list, for the Booking calendar card's name (the same read the panel makes).
  const calendars = useResource(
    useCallback(() => getStaffCalendars(staffId), [staffId]),
    [staffId],
  );

  // Each assigned location's week (the same schedule GET, per location). One location's
  // failed read marks only that location.
  const schedules = useResource(
    useCallback(async (): Promise<Map<string, WeekRead>> => {
      const results = await Promise.allSettled(
        assignedLocations.map((loc) => getStaffSchedule(staffId, loc.id)),
      );
      const map = new Map<string, WeekRead>();
      assignedLocations.forEach((loc, i) => {
        const result = results[i];
        map.set(
          loc.id,
          result?.status === 'fulfilled' ? { ok: true, days: result.value.days } : { ok: false },
        );
      });
      return map;
    }, [staffId, assignedLocations]),
    [staffId, assignedLocations],
  );

  // ---- Drawers ----
  const [drawer, setDrawer] = useState<StaffDrawer | null>(null);
  const editorRef = useRef<StaffEditorHandle>(null);
  const [calendarBlocked, setCalendarBlocked] = useState(false);
  const onCalendarStatus = useCallback(
    (status: { blocked: boolean }) => setCalendarBlocked(status.blocked),
    [],
  );
  // The location shown in the Weekly hours drawer.
  const [hoursLocationId, setHoursLocationId] = useState<string | null>(null);
  const shownHoursLocationId =
    hoursLocationId !== null && assignedLocations.some((l) => l.id === hoursLocationId)
      ? hoursLocationId
      : (assignedLocations[0]?.id ?? null);

  const refetchStaff = staff.refetch;
  const refetchAssigned = assigned.refetch;
  const refetchExceptions = exceptions.refetch;
  const refetchCaps = caps.refetch;
  const refetchSchedules = schedules.refetch;
  const refreshPage = useCallback(() => {
    refetchStaff();
    refetchAssigned();
    refetchExceptions();
    refetchCaps();
    refetchSchedules();
  }, [refetchStaff, refetchAssigned, refetchExceptions, refetchCaps, refetchSchedules]);

  const openDrawer = (which: StaffDrawer) => {
    setCalendarBlocked(false);
    setDrawer(which);
  };
  const closeDrawer = () => {
    setDrawer(null);
    setCalendarBlocked(false);
    // The page re-reads on close (the setup card follows what was saved).
    refreshPage();
  };
  const saveDrawer = async (): Promise<boolean> => {
    const handle = editorRef.current;
    if (handle && !(await handle.save())) return false;
    showToast('Changes saved', 'success');
    return true;
  };

  // ---- Header actions (More menu): the Staff list's actions, same calls ----
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [planLimit, setPlanLimit] = useState<PlanLimitInfo | null>(null);

  async function handleDeactivate(member: V2Staff) {
    setMenuAnchor(null);
    try {
      await deactivateStaff(member.id);
      refetchStaff();
      showToast('Staff member deactivated', 'success');
    } catch (err) {
      showToast(serverMessage(err) ?? 'Couldn’t deactivate the staff member.', 'error');
    }
  }

  async function handleReactivate(member: V2Staff) {
    setMenuAnchor(null);
    try {
      await reactivateStaff(member.id);
      refetchStaff();
      showToast('Staff member reactivated', 'success');
    } catch (err) {
      // The same branching as the Staff list (plan limit, removed elsewhere).
      const removedMessage = staffRemovedMessage(err, staffDisplayName(member));
      if (removedMessage !== null) {
        refetchStaff();
        showToast(removedMessage, 'error');
        return;
      }
      const code = v2ErrorCode(err);
      if (code === 'PLAN_LIMIT_REACHED') {
        setPlanLimit(parsePlanLimit(err));
        return;
      }
      if (code === 'PLAN_VERIFY_FAILED') {
        showToast('We couldn’t verify your plan just now. Please try again in a moment.', 'error');
        return;
      }
      showToast(serverMessage(err) ?? 'Couldn’t reactivate the staff member.', 'error');
    }
  }

  function handleRemoved(member: V2Staff, outcome: RemoveStaffOutcome) {
    setRemoveOpen(false);
    const name = staffDisplayName(member);
    showToast(
      outcome === 'removed-and-login-off'
        ? `${name} removed, and their login turned off.`
        : outcome === 'already-removed'
          ? `${name} was already removed.`
          : `${name} removed.`,
      'success',
    );
    navigate('/staff');
  }

  async function handleRestore(member: V2Staff) {
    setMenuAnchor(null);
    const name = staffDisplayName(member);
    try {
      await restoreStaff(member.id);
      refetchStaff();
      showToast(`${name} restored as inactive. Reactivate them to make them bookable.`, 'success');
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) refetchStaff();
      showToast(restoreErrorMessage(err, name), 'error');
    }
  }

  const member = staff.data;

  return (
    <Box>
      <MuiLink
        component={RouterLink}
        to="/staff"
        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 13, mb: 2 }}
      >
        <Icon name="arrow_back" sx={{ fontSize: 18 }} />
        All staff
      </MuiLink>

      {staff.status === 'loading' && !member && (
        <Skeleton
          variant="rounded"
          height={120}
          role="status"
          aria-label="Loading staff member"
          sx={{ borderRadius: 3, maxWidth: 560 }}
        />
      )}

      {staff.status === 'error' && (
        <ScreenError
          error={staff.error}
          what="the staff member"
          onRetry={staff.refetch}
          sx={{ maxWidth: 560 }}
          notFound={
            <Alert severity="warning" sx={{ maxWidth: 560 }}>
              That staff member wasn’t found. They may have been removed.{' '}
              <MuiLink component={RouterLink} to="/staff">
                Back to staff
              </MuiLink>
              .
            </Alert>
          }
        />
      )}

      {/* While the page re-reads after a drawer closes, the last values stay on screen. */}
      {staff.status !== 'error' && member && (
        <>
          <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, mb: 3 }}>
            <Typography variant="h6" component="h1">
              {staffDisplayName(member)}
            </Typography>
            {member.removedAt !== undefined ? (
              <Chip size="small" label="Removed" variant="outlined" />
            ) : (
              <Chip
                size="small"
                label={member.isActive ? 'Active' : 'Inactive'}
                color={member.isActive ? 'success' : 'default'}
                variant={member.isActive ? 'filled' : 'outlined'}
              />
            )}
            <Box sx={{ flex: 1 }} />
            <IconAction label="More actions" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              <Icon name="more_vert" />
            </IconAction>
            <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)}>
              {member.removedAt === undefined &&
                (member.isActive ? (
                  <MenuItem onClick={() => void handleDeactivate(member)}>Deactivate</MenuItem>
                ) : (
                  <MenuItem onClick={() => void handleReactivate(member)}>Reactivate</MenuItem>
                ))}
              {canManage && member.removedAt === undefined && (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    setRemoveOpen(true);
                  }}
                >
                  Remove
                </MenuItem>
              )}
              {canManage && member.removedAt !== undefined && (
                <MenuItem onClick={() => void handleRestore(member)}>Restore</MenuItem>
              )}
            </Menu>
          </Stack>

          <StaffSetupCard health={member.setupHealth} onFix={(item) => openDrawer(FIX_DRAWER[item])} />

          <Stack spacing={2} sx={{ maxWidth: 880, mb: 4 }}>
            {/* 1. Basics */}
            <SummaryCard title="Basics" onEdit={() => openDrawer('basics')}>
              <SummaryRow label="Name" value={staffDisplayName(member)} />
              <SummaryRow label="Email" value={member.email ?? NONE} />
              <SummaryRow label="Phone" value={member.phone ?? NONE} />
              <SummaryRow
                label="Login"
                last
                value={
                  member.authUserId === null ? (
                    <Stack sx={{ alignItems: 'flex-end' }}>
                      <Typography variant="body2">Not linked</Typography>
                      <MuiLink
                        component="button"
                        type="button"
                        onClick={() => openDrawer('basics')}
                        sx={{ fontSize: 13.5 }}
                      >
                        Link a login
                      </MuiLink>
                    </Stack>
                  ) : (
                    loginText(member.authUserId, logins.status === 'error' ? [] : loginOptions)
                  )
                }
              />
            </SummaryCard>

            {/* 2. Where they work */}
            <SummaryCard
              title="Where they work"
              onEdit={() => openDrawer('where')}
              empty={
                assigned.status === 'ready' && assignedLocations.length === 0
                  ? 'No locations yet'
                  : undefined
              }
            >
              <SummaryRow
                label="Locations"
                last
                value={
                  assigned.status === 'error'
                    ? COULD_NOT_LOAD
                    : assignedData === null
                      ? LOADING
                      : assignedLocations.map(locationLabel).join(', ')
                }
              />
            </SummaryCard>

            {/* 3. Weekly hours */}
            <SummaryCard
              title="Weekly hours"
              onEdit={() => openDrawer('hours')}
              editDisabled={assignedLocations.length === 0}
              empty={
                assigned.status === 'ready' && assignedLocations.length === 0
                  ? 'No hours. Add a location in Where they work first.'
                  : undefined
              }
            >
              {assigned.status === 'error' && <SummaryRow label="Hours" value={COULD_NOT_LOAD} last />}
              {assigned.status !== 'error' && assignedData === null && (
                <SummaryRow label="Hours" value={LOADING} last />
              )}
              {assignedLocations.map((loc, i) => {
                const week = schedules.data?.get(loc.id);
                const value =
                  week === undefined
                    ? schedules.status === 'error'
                      ? COULD_NOT_LOAD
                      : LOADING
                    : week.ok
                      ? weeklyHoursSummary(week.days)
                      : COULD_NOT_LOAD;
                return (
                  <SummaryRow
                    key={loc.id}
                    label={locationLabel(loc)}
                    value={value}
                    last={i === assignedLocations.length - 1}
                  />
                );
              })}
            </SummaryCard>

            {/* 4. Days off */}
            <SummaryCard
              title="Days off"
              onEdit={() => openDrawer('daysOff')}
              editDisabled={exceptions.data === null}
              empty={
                exceptions.status === 'ready' && exceptions.data?.length === 0
                  ? 'No days off'
                  : undefined
              }
            >
              {exceptions.data === null ? (
                <SummaryRow
                  label="Days off"
                  value={exceptions.status === 'error' ? COULD_NOT_LOAD : LOADING}
                  last
                />
              ) : (
                exceptions.data.map((exception, i, all) => (
                  <SummaryRow
                    key={exception.id ?? `${exception.exceptionDate}-${i}`}
                    label={dayOffDate(exception)}
                    value={dayOffLine(exception, (id) => {
                      const loc = assignedLocations.find((l) => l.id === id);
                      return loc ? locationLabel(loc) : id;
                    })}
                    last={i === all.length - 1}
                  />
                ))
              )}
            </SummaryCard>

            {/* 5. Booking calendar */}
            <SummaryCard title="Booking calendar" onEdit={() => openDrawer('calendar')}>
              <SummaryRow
                label="Calendar"
                value={(() => {
                  if (member.calendarId === null) return 'None';
                  if (calendars.status === 'error') return COULD_NOT_LOAD;
                  if (calendars.data === null) return LOADING;
                  const found = calendars.data.calendars.find((c) => c.id === member.calendarId);
                  return found ? scrubBrand(found.name) : 'Saved calendar not found. Choose it again.';
                })()}
              />
              <SummaryRow label="Sync" value={member.calendarSyncEnabled ? 'On' : 'Off'} last />
            </SummaryCard>

            {/* 6. Limits */}
            <SummaryCard
              title="Limits"
              onEdit={() => openDrawer('limits')}
              editDisabled={caps.data === null}
              empty={caps.status === 'ready' && caps.data?.length === 0 ? 'No limits' : undefined}
            >
              {caps.data === null ? (
                <SummaryRow
                  label="Appointment limits"
                  value={caps.status === 'error' ? COULD_NOT_LOAD : LOADING}
                  last
                />
              ) : (
                caps.data.map((cap, i, all) => (
                  <SummaryRow
                    key={cap.period}
                    label={CAP_PERIOD_LABELS[cap.period]}
                    value={capLine(cap)}
                    last={i === all.length - 1}
                  />
                ))
              )}
            </SummaryCard>

            {/* 7. Services they teach */}
            <SummaryCard
              title="Services they teach"
              onEdit={() => navigate('/services')}
              empty={member.services?.length === 0 ? 'None yet' : undefined}
            >
              <SummaryRow
                label="Services"
                last
                value={
                  member.services === undefined ? (
                    NONE
                  ) : (
                    <Typography variant="body2">
                      {member.services.map((svc, i) => (
                        <span key={svc.id}>
                          {i > 0 && ', '}
                          <MuiLink component={RouterLink} to={`/services/${svc.id}`}>
                            {svc.name}
                          </MuiLink>
                        </span>
                      ))}
                    </Typography>
                  )
                }
              />
            </SummaryCard>
          </Stack>

          <EditDrawer
            open={drawer !== null}
            title={drawer ? DRAWER_TITLES[drawer] : ''}
            onClose={closeDrawer}
            onSave={saveDrawer}
            doneOnly={drawer === 'daysOff'}
            saveDisabled={
              (drawer === 'calendar' && calendarBlocked) ||
              (drawer === 'hours' &&
                (shownHoursLocationId === null ||
                  schedules.data?.get(shownHoursLocationId)?.ok !== true))
            }
          >
            {(drawer === 'basics' || drawer === 'where') && (
              <StaffFields
                staff={member}
                section={drawer}
                handleRef={editorRef}
                showToast={showToast}
                // Reuse the roster this screen already resolved — no second fetch. Still
                // loading (or failed) ⇒ `undefined` ⇒ the form loads its own.
                loginOptions={loginOptions ?? undefined}
              />
            )}

            {drawer === 'hours' && shownHoursLocationId === null && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Add a location in Where they work first. Hours are set per location.
              </Typography>
            )}
            {drawer === 'hours' && shownHoursLocationId !== null && (
              <HoursDrawerBody
                locations={assignedLocations}
                locationId={shownHoursLocationId}
                onLocationChange={setHoursLocationId}
                week={schedules.data?.get(shownHoursLocationId)}
                failed={schedules.status === 'error'}
                onRetry={refetchSchedules}
              >
                {(days) => (
                  <StaffScheduleGrid
                    key={shownHoursLocationId}
                    days={days}
                    staffId={staffId}
                    locationId={shownHoursLocationId}
                    refetch={refetchSchedules}
                    showToast={showToast}
                    embedded
                    handleRef={editorRef}
                  />
                )}
              </HoursDrawerBody>
            )}

            {drawer === 'daysOff' && exceptions.data !== null && (
              <>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                  Mark this staff member off on a specific date, or set special hours, at all
                  locations or one. Each change saves when you add or remove it.
                </Typography>
                <StaffDateExceptionsPanel
                  exceptions={exceptions.data}
                  staffId={staffId}
                  locations={assignedLocations}
                  refetch={refetchExceptions}
                  showToast={showToast}
                />
              </>
            )}

            {drawer === 'calendar' && (
              <StaffCalendarSyncPanel
                // Remount when the saved calendar fields change so the form reseeds.
                key={`cal|${member.calendarId ?? ''}|${String(member.calendarSyncEnabled)}`}
                staff={member}
                refetch={refetchStaff}
                showToast={showToast}
                embedded
                handleRef={editorRef}
                onStatusChange={onCalendarStatus}
              />
            )}

            {drawer === 'limits' && caps.data !== null && (
              <>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
                  Limit how many appointments this staff member can take per day, week, or month.
                  Leave a period blank for no limit.
                </Typography>
                <StaffCapsPanel
                  caps={caps.data}
                  staffId={staffId}
                  refetch={refetchCaps}
                  showToast={showToast}
                  embedded
                  handleRef={editorRef}
                />
              </>
            )}
          </EditDrawer>

          {removeOpen && (
            <RemoveStaffDialog
              key={member.id}
              staff={member}
              onClose={() => {
                setRemoveOpen(false);
                refetchStaff();
              }}
              onRemoved={(outcome) => handleRemoved(member, outcome)}
              onRemovedLoginStillActive={refetchStaff}
            />
          )}

          <PlanLimitDialog
            open={planLimit !== null}
            info={planLimit}
            onClose={() => setPlanLimit(null)}
          />
        </>
      )}
    </Box>
  );
}

/** The Weekly hours drawer: a tab per location, then that location's week (or why not). */
function HoursDrawerBody({
  locations,
  locationId,
  onLocationChange,
  week,
  failed,
  onRetry,
  children,
}: {
  locations: V2Location[];
  locationId: string;
  onLocationChange: (id: string) => void;
  week: WeekRead | undefined;
  failed: boolean;
  onRetry: () => void;
  children: (days: StaffScheduleDay[]) => ReactNode;
}) {
  return (
    <Box>
      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
        Set the weekly hours for each location this staff member works at, in that location’s
        local time. Save saves the location shown.
      </Typography>
      {locations.length > 1 && (
        <Tabs
          value={locationId}
          onChange={(_e, value: string) => onLocationChange(value)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ mb: 2, borderBottom: '1px solid', borderColor: 'divider' }}
          aria-label="Schedule location"
        >
          {locations.map((loc) => (
            <Tab key={loc.id} value={loc.id} label={locationLabel(loc)} />
          ))}
        </Tabs>
      )}
      {week === undefined && !failed && (
        <Skeleton variant="rounded" height={360} sx={{ borderRadius: 3 }} />
      )}
      {(week?.ok === false || (week === undefined && failed)) && (
        <ScreenError
          error={null}
          what="the schedule for this location"
          onRetry={onRetry}
          sx={{ alignItems: 'center' }}
        />
      )}
      {week?.ok === true && children(week.days)}
    </Box>
  );
}
